import { useId } from 'react';
import Flags from 'country-flag-icons/react/3x2';
import { Control, Controller, FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import PhoneInput from 'react-phone-number-input';

import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputProps,
  Tooltip,
} from '@chakra-ui/react';
import { InfoIcon, WarningIcon } from '@udacity/chakra-uds-icons';

import { getViewerCountryCode } from '~/features/internationalization/utils/get-viewer-geo-codes';
import { useClientRender } from '~/hooks/use-client-render';

import 'react-phone-number-input/style.css';

export type FormTelephoneInputProps = InputProps & {
  label: string;
  helperText?: string;
  infoText?: string;
  errorMessage?: string | FieldError | Merge<FieldError, FieldErrorsImpl<object>> | undefined;
  isDisabled?: boolean;
  control?: Control<any, object>;
  name: string;
};

export function FormTelephoneInput(props: FormTelephoneInputProps) {
  const { label, errorMessage, helperText, infoText, isDisabled, control, name } = props;

  const isClient = useClientRender();
  const defaultCountry = isClient ? getViewerCountryCode() : undefined;
  const controlId = useId();

  return (
    <FormControl id={controlId} isDisabled={isDisabled} isInvalid={Boolean(errorMessage)}>
      <Flex>
        <FormLabel mb={helperText ? 0 : '8px'} data-peer>
          {label}
        </FormLabel>
        {infoText && (
          <Tooltip label={infoText} hasArrow>
            <InfoIcon color='black' height='24px' width='24px' />
          </Tooltip>
        )}
      </Flex>
      {helperText && <FormHelperText margin='0 0 8px 0'>{helperText}</FormHelperText>}
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <PhoneInput
            aria-label='Phone Number'
            defaultCountry={(defaultCountry || 'US') as any}
            flags={Flags}
            inputComponent={Input}
            placeholder='Phone Number'
            style={{ flex: 1 }}
            numberInputProps={{
              id: controlId,
            }}
            {...field}
          />
        )}
      />
      {errorMessage && (
        <FormErrorMessage mt='8px'>
          <>
            <WarningIcon h='26px' w='26px' />
            {errorMessage}
          </>
        </FormErrorMessage>
      )}
    </FormControl>
  );
}
